// Sync diagnostics are deliberately independent of Dexie and never accept payloads.
const STORAGE_KEY = 'agroel:sync-diagnostics:v1'
const MAX_EVENTS = 80
const PHASES = new Set(['bootstrap', 'release', 'database', 'login', 'refresh', 'profile', 'revision', 'waiting-export', 'waiting-lock', 'preflight', 'checking', 'download', 'decompress', 'parse', 'validate', 'normalize', 'apply', 'commit', 'finalize', 'snapshot', 'ack', 'verify', 'done', 'failed'])
const NUMERIC_FIELDS = ['duration_ms', 'bytes_loaded', 'bytes_total', 'decoded_bytes', 'rows_done', 'rows_total', 'http_status']
const listeners = new Set()
let events = []
let currentRunId = ''
let startedAt = 0
let phaseStartedAt = 0
let previousPhase = ''
let currentState = null
let lastRequestId = null

const REQUEST_ID_RE = /^(?:[0-9a-f]{16,64}|[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})$/i
function identifier() {
  const cryptoApi = typeof window !== 'undefined' ? window.crypto : null
  if (cryptoApi?.randomUUID) return cryptoApi.randomUUID()
  if (cryptoApi?.getRandomValues) {
    const bytes = cryptoApi.getRandomValues(new Uint8Array(16))
    return Array.from(bytes, byte => byte.toString(16).padStart(2, '0')).join('')
  }
  return Array.from({ length: 32 }, () => Math.floor(Math.random() * 16).toString(16)).join('')
}

export function safeSyncErrorCode(error) {
  const candidate = String(error?.code || error?.name || error || 'SYNC_FAILED')
  // Error messages may contain responses, URLs or credentials. Only codes pass.
  return /^[A-Za-z][A-Za-z0-9_:-]{0,79}$/.test(candidate) ? candidate : 'SYNC_FAILED'
}

// Rehydrate only the same allowlisted metadata, including after an interrupted start.
try {
  const stored = JSON.parse(sessionStorage.getItem(STORAGE_KEY) || '[]')
  if (Array.isArray(stored)) events = stored.slice(-MAX_EVENTS).flatMap(row => {
    if (!REQUEST_ID_RE.test(String(row?.sync_run_id || '')) || !PHASES.has(row?.phase)) return []
    const event = { sync_run_id: row.sync_run_id, phase: row.phase }
    for (const key of [...NUMERIC_FIELDS, 'at', 'elapsed_ms', 'previous_phase_duration_ms']) {
      if (typeof row[key] === 'number' && Number.isFinite(row[key]) && row[key] >= 0) event[key] = row[key]
    }
    if (row.error_code) event.error_code = safeSyncErrorCode(row.error_code)
    if (REQUEST_ID_RE.test(String(row.request_id || ''))) event.request_id = row.request_id
    if (row.committed === true) event.committed = true
    return [event]
  })
} catch { /* storage unavailable, invalid or full */ }

export function beginSyncDiagnosticRun(phase = 'preflight') {
  currentRunId = identifier()
  startedAt = phaseStartedAt = Date.now()
  previousPhase = ''
  currentState = null
  lastRequestId = null
  recordSyncDiagnostic(phase)
  return currentRunId
}

export function recordSyncDiagnostic(phase, detail = {}) {
  const safePhase = PHASES.has(phase) ? phase : 'preflight'
  if (!currentRunId) return beginSyncDiagnosticRun(safePhase)
  const now = Date.now()
  const event = { sync_run_id: currentRunId, phase: safePhase, at: now, elapsed_ms: now - startedAt }
  if (previousPhase !== safePhase) {
    event.previous_phase_duration_ms = now - phaseStartedAt
    phaseStartedAt = now
    previousPhase = safePhase
  }
  for (const key of NUMERIC_FIELDS) {
    const value = detail?.[key]
    if (typeof value === 'number' && Number.isFinite(value) && value >= 0) event[key] = value
  }
  if (typeof window !== 'undefined') {
    const meta = window.__AGROEL_BUNDLE_META__ || window.__AGROEL_RELEASE_META__
    if (/^\d+\.\d+\.\d+$/.test(String(meta?.release_version || ''))) event.release_version = meta.release_version
    if (Number.isSafeInteger(meta?.dexie_schema_version)) event.schema_version = meta.dexie_schema_version
  }
  if (REQUEST_ID_RE.test(String(detail?.request_id || ''))) lastRequestId = detail.request_id
  if (lastRequestId) event.request_id = lastRequestId
  if (detail?.committed === true) event.committed = true
  if (detail?.error_code) event.error_code = safeSyncErrorCode(detail.error_code)
  if (!currentState || !['done', 'failed'].includes(currentState.phase)) currentState = event
  events.push(event)
  events = events.slice(-MAX_EVENTS)
  renderEarlySyncDiagnostics(event)
  try { sessionStorage.setItem(STORAGE_KEY, JSON.stringify(events)) } catch { /* quota/private mode */ }
  for (const listener of listeners) {
    try { listener(readSyncDiagnostics()) } catch { /* diagnostics must not break sync */ }
  }
  return event
}

export function readSyncDiagnostics() {
  return events.map(event => ({ ...event }))
}

export function readSyncDiagnosticState() {
  return currentState ? { ...currentState } : null
}

export function subscribeSyncDiagnostics(listener) {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

export function isSyncDebugEnabled() {
  try { return new URLSearchParams(window.location.search).get('debugSync') === '1' } catch { return false }
}

export function getSyncCorrelationHeaders() {
  return {
    ...(currentRunId ? { 'X-Agroel-Sync-Run-ID': currentRunId } : {}),
    'X-Request-ID': identifier()
  }
}

export function syncErrorTranslationKey(error) {
  const code = safeSyncErrorCode(error).toLowerCase()
  if (/quota/.test(code)) return 'app.syncErrorQuota'
  if (/upgrade|version|schema|dexie_open|db_/.test(code)) return 'app.syncErrorDatabase'
  if (/dataerror|constraint|invalid|parse|json|gzip/.test(code)) return 'app.syncErrorData'
  if (/timeout|stalled/.test(code)) return 'app.syncErrorTimeout'
  if (/network|econn|offline/.test(code)) return 'app.syncErrorNetwork'
  return 'app.syncErrorGeneric'
}

function renderEarlySyncDiagnostics(event) {
  if (!isSyncDebugEnabled() || typeof document === 'undefined' || !document.body || window.__AGROEL_APP_MOUNTED__) return
  let panel = document.getElementById('agroel-sync-early-diagnostics')
  if (!panel) {
    panel = document.createElement('aside')
    panel.id = 'agroel-sync-early-diagnostics'
    panel.setAttribute('data-testid', 'sync-diagnostics')
    panel.style.cssText = 'position:fixed;bottom:0;left:4px;right:4px;z-index:2147483647;padding:10px;background:#132332;color:white;font:12px monospace;overflow-wrap:anywhere;pointer-events:none'
    document.body.appendChild(panel)
  }
  panel.textContent = `${event.sync_run_id} · ${event.phase} · ${Math.round(event.elapsed_ms / 1000)} s` +
    (event.bytes_loaded !== undefined ? ` · ${event.bytes_loaded}/${event.bytes_total || '?'} B` : '') +
    (event.error_code ? ` · ${event.error_code}` : '') +
    (event.request_id ? ` · HTTP ID ${event.request_id}` : '')
}

export function renderBootstrapFailure(error) {
  recordSyncDiagnostic('failed', { error_code: safeSyncErrorCode(error) })
  if (typeof document === 'undefined') return
  const host = document.getElementById('app') || document.body
  if (!host) return
  const panel = document.createElement('section')
  panel.setAttribute('role', 'alert')
  panel.style.cssText = 'margin:10vh auto;padding:24px;max-width:540px;font:17px system-ui;color:#172b35;background:#fff;border:1px solid #cbd5e1;border-radius:12px'
  const english = typeof navigator !== 'undefined' && /^en\b/i.test(navigator.language || '')
  const title = document.createElement('h1')
  title.textContent = 'Agroel'
  const message = document.createElement('p')
  message.textContent = english ? 'The application could not start. Check your connection and try again. Local offline data has been preserved.' : 'Aplikaci se nepodařilo spustit. Zkontrolujte připojení a zkuste to znovu. Místní offline data zůstala zachovaná.'
  const code = document.createElement('p')
  code.textContent = `${safeSyncErrorCode(error)} · ${currentRunId}`
  const retry = document.createElement('button')
  retry.type = 'button'
  retry.textContent = english ? 'Try again' : 'Zkusit znovu'
  retry.onclick = () => window.location.reload()
  const login = document.createElement('button')
  login.type = 'button'
  login.textContent = english ? 'Return to sign in' : 'Zpět na přihlášení'
  login.onclick = () => {
    for (const storage of [localStorage, sessionStorage]) {
      for (const key of ['user', 'access_token', 'refresh_token', 'display_session', 'display_scope', 'offline_session']) {
        try { storage.removeItem(key) } catch { /* unavailable storage */ }
      }
    }
    window.location.assign('/login')
  }
  panel.append(title, message, code, retry, login)
  host.replaceChildren(panel)
}

export function withBootstrapTimeout(task, timeoutMs = 30000, code = 'BOOTSTRAP_TIMEOUT') {
  let timer
  return Promise.race([
    Promise.resolve(task),
    new Promise((_, reject) => { timer = setTimeout(() => reject(Object.assign(new Error(code), { code })), timeoutMs) })
  ]).finally(() => clearTimeout(timer))
}

// This read-only hook exposes the same filtered records as the mobile panel.
if (typeof window !== 'undefined' && isSyncDebugEnabled()) {
  window.agroelSyncDebug = Object.freeze({
    log: readSyncDiagnostics,
    state: readSyncDiagnosticState
  })
}
